import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { scarveProducts } from '@/mocks/products';

function RevealDiv({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('ew-revealed'); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`ew-reveal ${className ?? ''}`} style={style}>{children}</div>;
}

const scarves = scarveProducts;

export default function Scarves() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative py-24 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://public.readdy.ai/ai/img_res/28d4d710f38e6a9c56fe589f735f0d31.jpg" alt="Scarves hero" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/60"></div>
          </div>
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
            <p style={{ fontFamily: '"Dancing Script", cursive', color: '#E8B96A', fontSize: '1.3rem' }} className="mb-2">Flowing. Colorful. Endlessly Versatile.</p>
            <h1 style={{ fontFamily: '"Playfair Display", serif', color: '#fff' }} className="text-5xl md:text-6xl font-black mb-4">Scarves &amp; Wraps</h1>
            <p style={{ fontFamily: '"Lato", sans-serif', color: '#EDE5D8', lineHeight: 1.8 }} className="text-lg mb-8">
              The perfect finishing touch for any goddess outfit. Pair with our Magic Dresses, jackets, or wear solo — the possibilities are endless.
            </p>
            <a href="#scarf-grid" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
              Shop Scarves
            </a>
          </div>
        </section>

        {/* Grid */}
        <section id="scarf-grid" className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <RevealDiv className="text-center mb-12">
              <p style={{ fontFamily: '"Dancing Script", cursive', color: '#C9933A', fontSize: '1.2rem' }}>Wrap yourself in color</p>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-4xl font-bold mt-1">Our Scarf Collection</h2>
            </RevealDiv>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {scarves.map((s, i) => (
                <RevealDiv key={s.id} style={{ transitionDelay: `${i * 0.08}s` }}>
                  <div className="rounded-2xl overflow-hidden group cursor-pointer" style={{ backgroundColor: '#fff' }}>
                    <div className="overflow-hidden" style={{ height: '280px' }}>
                      <img src={s.img} alt={s.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-full" style={{ backgroundColor: '#EDE5D8', color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>{s.material}</span>
                      </div>
                      <h3 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-lg font-bold mb-1">{s.title}</h3>
                      <p style={{ fontFamily: '"Lato", sans-serif', color: '#C9933A', fontWeight: 700 }} className="text-sm mb-1">{s.price}</p>
                      <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42' }} className="text-sm mb-3">{s.material}</p>
                      <a href="mailto:earthwisdom80@gmail.com" className="inline-flex items-center gap-1.5 text-sm font-bold whitespace-nowrap" style={{ color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>
                        Inquire to Order <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                  </div>
                </RevealDiv>
              ))}
            </div>
          </div>
        </section>

        {/* Related products */}
        <RevealDiv>
          <section className="py-14" style={{ backgroundColor: '#F5F0E8' }}>
            <div className="max-w-4xl mx-auto px-4 text-center">
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-2xl font-bold mb-4">Complete the Look</h2>
              <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42' }} className="text-base mb-8">Scarves pair beautifully with our Magic Dresses and Goddess Jackets.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/magic-dresses" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#1E4F4F', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
                  Shop Magic Dresses
                </Link>
                <Link to="/jackets" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
                  Shop Jackets
                </Link>
              </div>
            </div>
          </section>
        </RevealDiv>
      </main>
      <Footer />
    </div>
  );
}
